import {
  DepositLocation,
  LocalOfferLocation,
  LocalOfferMarker,
  WithdrawalLocation,
} from '@varo/graphql-schema-types';
import {
  AdvancedMarker,
  APIProvider,
  Map,
  MapCameraChangedEvent,
  MapCameraProps,
  Marker,
} from '@vis.gl/react-google-maps';
import { LocationObject } from 'expo-location';
import { useRouter } from 'expo-router';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import MapLoadingIndicator from 'shared/components/maps/MapLoadingIndicator/MapLoadingIndicator';
import RequestLocationBottomSheet from 'shared/components/maps/RequestLocationBottomSheet/RequestLocationBottomSheet';
import Icon, { IconKeyType } from 'shared/components/vxl/icon/Icon/Icon';
import VXLSearchField from 'shared/components/vxl/input/VXLSearchField/VXLSearchField';
import MapNode, { MapNodeProps } from 'shared/components/vxl/MapNode/MapNode';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import useClusteredMarkers from 'shared/hooks/maps/useClusteredMarkers';
import useMarkerById from 'shared/hooks/maps/useMarkerById';
import useZipCodeCoordinates from 'shared/hooks/maps/useZipCodeCoordinates';
import { AbortError } from 'shared/hooks/useCancellableMutation/useCancellableMutation';
import {
  EnvironmentContext,
  EnvironmentContextProps,
} from 'shared/providers/environment';
import theme from 'shared/styles/theme';
import getEnvironmentVar from 'shared/utils/getEnvironmentVar';
import fetchPredictionDetails from 'shared/utils/maps/fetchPredictionDetails';
import fetchPredictions from 'shared/utils/maps/fetchPredictions';
import getMarkerZIndex from 'shared/utils/maps/getMarkerZIndex';
import {
  DefaultMapZoom,
  WebMapControlProps,
} from 'shared/utils/maps/mapConstants';

import {
  getWebIcon,
  PlaceDetailsResponse,
  PredictionMapViewProps,
  PredictionResponse,
  PredictionType,
} from './PredictionMapView.helpers';

const MARKER_ICON_MAP: Record<LocalOfferMarker, IconKeyType> = {
  [LocalOfferMarker.Merchant]: 'STORE',
  [LocalOfferMarker.Food]: 'FOOD',
  [LocalOfferMarker.Entertainment]: 'ENTERTAINMENT',
  [LocalOfferMarker.Travel]: 'TRAVEL',
  [LocalOfferMarker.Shopping]: 'SHOPPING',
  [LocalOfferMarker.UndefinedMarker]: 'location',
  [LocalOfferMarker.Gas]: 'GAS',
};

const isLocalOfferLocation = (
  loc: WithdrawalLocation | DepositLocation | LocalOfferLocation,
): loc is LocalOfferLocation => {
  return 'marker' in loc && loc.marker !== undefined;
};

const getMarkerIcon = (
  marker: WithdrawalLocation | DepositLocation | LocalOfferLocation,
): IconKeyType => {
  if (isLocalOfferLocation(marker)) {
    return MARKER_ICON_MAP[marker.marker] ?? 'location';
  }
  return 'location';
};

const mapMarkerToMapNodeProps = (
  marker: WithdrawalLocation | DepositLocation | LocalOfferLocation,
  isSelected: boolean,
  onPress: () => void,
): MapNodeProps => ({
  iconKey: getMarkerIcon(marker),
  metric: isLocalOfferLocation(marker) ? (marker.cashbackAmount ?? '0') : '0',
  labelText: marker.name ?? '',
  priceText: isLocalOfferLocation(marker) ? (marker.label ?? '') : '',
  selected: isSelected,
  analyticsId: marker.id,
  onPress,
});

export default function <T extends WithdrawalLocation | DepositLocation>({
  navigationBarTitle,
  onPredictionSelected,
  isLoading = false,
  markerData = [],
  onLocationChange,
  onMarkerPress,
  requestLocationTitle,
  requestLocationBody,
  showMapSearchBar = true,
  filterComponent,
  onSearchValueChange,
  shouldShowSearchThisAreaButtonVisible = true,
  usePillAnnotations = false,
  defaultZoom = DefaultMapZoom.l,
  selectedLocationId,
}: PredictionMapViewProps<T>) {
  const { t: translatedText } = useTranslation('maps', {
    keyPrefix: 'predictionMap',
  });
  const router = useRouter();
  const getZipCodeCoordinates = useZipCodeCoordinates();
  const { currentEnv, getMapsUrl } =
    useContext<EnvironmentContextProps>(EnvironmentContext);
  const mapsUrl = getMapsUrl(currentEnv);
  const [isSearchThisAreaButtonVisible, setIsSearchThisAreaButtonVisible] =
    useState(false);
  const [cameraLocation, setCameraLocation] = useState<MapCameraProps>();
  const [searchLocation, setSearchLocation] =
    useState<google.maps.LatLngLiteral>();
  const [search, setSearch] = useState('');
  const [predictions, setPredictions] = useState<PredictionType[]>([]);
  const [isPredictionsLoading, setIsPredictionsLoading] = useState(false);
  const isInitialLoad = useRef(true);
  const hasInitializedLocation = useRef(false);
  const [selectedAddress, setSelectedAddress] = useState('');

  const addressSelectedEvent = usePressableAnalytics({
    analyticsId: 'addressSelected',
    customAttributes: { address_selected: search },
  });

  useEffect(() => {
    if (selectedAddress) {
      addressSelectedEvent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddress]);

  const findAtmSearchAreaEvent = usePressableAnalytics({
    analyticsId: 'findAtmSearchClicked',
  });

  const handleLocation = useCallback(
    (position: LocationObject | GeolocationPosition) => {
      if (hasInitializedLocation.current) {
        return;
      }

      const newCoords = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };
      const initialCamera = {
        center: newCoords,
        zoom: defaultZoom,
      };

      setCameraLocation(initialCamera);
      hasInitializedLocation.current = true;
      onLocationChange(newCoords, true);
    },
    [onLocationChange, defaultZoom],
  );
  const handleDeniedLocation = useCallback(async () => {
    if (hasInitializedLocation.current) {
      return;
    }

    try {
      const newCoords = await getZipCodeCoordinates(mapsUrl);

      if (newCoords) {
        setCameraLocation({
          center: newCoords,
          zoom: defaultZoom,
        });
        hasInitializedLocation.current = true;
        onLocationChange(newCoords, true);
      }
    } catch (e) {
      if (e instanceof AbortError) {
        return;
      }

      console.warn('Error fetching zip: ', e);
    }
  }, [getZipCodeCoordinates, mapsUrl, onLocationChange, defaultZoom]);

  const handleValueChange = async (value: string) => {
    setIsPredictionsLoading(true);
    setSearch(value);

    // Don't make an API request if we don't have any search value
    if (value.trim() === '') {
      setIsPredictionsLoading(false);
      return;
    }

    try {
      const request = await fetchPredictions(value, mapsUrl);
      const predictionResponse = (await request.json()) as PredictionResponse;

      if (predictionResponse) {
        setPredictions(predictionResponse.predictions);
      }
    } catch (e) {
      console.error('Error fetching predictions: ', e);
    } finally {
      setIsPredictionsLoading(false);
    }
    onSearchValueChange?.(value);
  };

  const handlePredictionTapped = async (prediction: PredictionType) => {
    setSearch(prediction.description);
    setSelectedAddress(prediction.description);

    if (prediction) {
      try {
        const request = await fetchPredictionDetails(
          prediction.place_id,
          mapsUrl,
        );

        const placeDetailsResponse =
          (await request.json()) as PlaceDetailsResponse;

        if (placeDetailsResponse) {
          const newLat = parseFloat(
            placeDetailsResponse?.result?.geometry?.location?.lat,
          );
          const newLng = parseFloat(
            placeDetailsResponse?.result?.geometry?.location?.lng,
          );

          const newCoords = { lat: newLat, lng: newLng };

          setCameraLocation({
            center: newCoords,
            zoom: defaultZoom,
          });

          await onPredictionSelected(placeDetailsResponse);
        }
      } catch (e) {
        console.error('Error fetching prediction details: ', e);
      }
    }
  };
  const handleCameraChange = useCallback(
    (ev: MapCameraChangedEvent) => {
      if (isInitialLoad.current) {
        isInitialLoad.current = false;
      } else {
        setIsSearchThisAreaButtonVisible(true);
      }

      setSearchLocation(ev.detail.center);
      setCameraLocation(ev.detail);
    },
    [setCameraLocation],
  );
  const handleSearchThisAreaPress = () => {
    findAtmSearchAreaEvent();
    setIsSearchThisAreaButtonVisible(false);
    if (searchLocation?.lat && searchLocation?.lng) {
      onLocationChange(
        {
          lat: searchLocation?.lat,
          lng: searchLocation?.lng,
        },
        true,
      );
    }
  };

  const title = navigationBarTitle || '';
  const hasTitle = title.trim().length > 0;
  const shouldShowNavigationBar = hasTitle || showMapSearchBar;

  const clusteredMarkers = useClusteredMarkers(
    usePillAnnotations ? (markerData ?? []) : [],
  );

  const originalMarkerById = useMarkerById(markerData);

  return (
    <View style={styles.container}>
      {shouldShowNavigationBar ? (
        <NavigationBar
          title={navigationBarTitle}
          primaryIcon={<Icon iconKey="BACK" iconSize="MD" />}
          primaryOnPress={() => router.back()}
          accessory={
            showMapSearchBar ? (
              <VXLSearchField
                options={predictions.map((item) => ({
                  key: item.place_id,
                  label: item.description,
                  value: item,
                }))}
                onSelectOption={handlePredictionTapped}
                placeholder={translatedText('searchPlaceholder')}
                onChangeText={handleValueChange}
                value={search}
                isLoading={isPredictionsLoading}
                analyticsId="atmSearch"
              />
            ) : null
          }
        />
      ) : null}
      {filterComponent}
      <View style={styles.mapContainer}>
        {!cameraLocation || isLoading ? <MapLoadingIndicator /> : null}
        {cameraLocation ? (
          <APIProvider
            apiKey={getEnvironmentVar('EXPO_GOOGLE_MAPS_WEB_API_KEY') as string}
          >
            <Map
              {...cameraLocation}
              defaultZoom={defaultZoom}
              onCameraChanged={handleCameraChange}
              {...WebMapControlProps}
              style={{
                paddingTop: theme.spacing.m,
              }}
            >
              {usePillAnnotations
                ? clusteredMarkers.map((marker) => {
                    const isSelected = selectedLocationId === marker.id;
                    const hasHiddenIcon = (marker as any).hideIcon;

                    return (
                      <AdvancedMarker
                        key={marker.id}
                        position={{
                          lat: marker.coordinate.latitude,
                          lng: marker.coordinate.longitude,
                        }}
                        zIndex={getMarkerZIndex({ isSelected, hasHiddenIcon })}
                        onClick={() => {
                          const originalMarker = (originalMarkerById.get(
                            marker.id,
                          ) ?? marker) as T;
                          onMarkerPress(originalMarker);
                        }}
                      >
                        <MapNode
                          {...mapMarkerToMapNodeProps(
                            marker as any,
                            isSelected,
                            () => {
                              const originalMarker = (originalMarkerById.get(
                                marker.id,
                              ) ?? marker) as T;
                              onMarkerPress(originalMarker as any);
                            },
                          )}
                          hideIcon={!isSelected && hasHiddenIcon}
                        />
                      </AdvancedMarker>
                    );
                  })
                : markerData?.map((marker) => (
                    <Marker
                      key={marker.id}
                      position={{
                        lat: marker.coordinate.latitude,
                        lng: marker.coordinate.longitude,
                      }}
                      icon={{
                        url:
                          'data:image/svg+xml;charset=utf-8,' +
                          encodeURIComponent(
                            getWebIcon(
                              marker,
                              selectedLocationId === marker.id,
                            ),
                          ),
                      }}
                      onClick={() => {
                        onMarkerPress(marker);
                      }}
                    />
                  ))}
            </Map>
          </APIProvider>
        ) : null}

        {shouldShowSearchThisAreaButtonVisible &&
        isSearchThisAreaButtonVisible ? (
          <View style={styles.searchThisAreaContainer}>
            <Pressable
              style={styles.searchThisAreaButton}
              onPress={handleSearchThisAreaPress}
            >
              <BodySmBoldText style={styles.searchThisAreaButtonText}>
                {translatedText('searchThisAreaButton')}
              </BodySmBoldText>
            </Pressable>
          </View>
        ) : null}

        <RequestLocationBottomSheet
          onLocation={handleLocation}
          onDeniedLocation={handleDeniedLocation}
          title={requestLocationTitle}
          body={requestLocationBody}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  mapContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    zIndex: -1,
  },
  searchThisAreaButton: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    borderRadius: 40,
    flexDirection: 'row',
    top: 15,
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    shadowColor: 'rgba(0, 0, 0, 0.12)',
    shadowOffset: { height: 2, width: 0 },
    shadowOpacity: 1,
    shadowRadius: 8,
  },
  searchThisAreaButtonText: {
    color: theme.color.interactive.primary,
    textAlign: 'center',
  },
  searchThisAreaContainer: {
    alignItems: 'center',
    left: 0,
    position: 'absolute',
    right: 0,
    top: theme.spacing.m,
  },
});
